// src/services/ConfigService.js - NEW FILE
// Secure configuration management following your established service pattern

import { ErrorTrackingService } from './ErrorTrackingService';

export class ConfigService {
  static isInitialized = false;
  static config = {
    firebase: null,
    environment: null,
    validation: {
      enabled: true,
      strict: false
    }
  };

  // Initialize configuration service (non-breaking)
  static initialize() {
    try {
      if (this.isInitialized) return true;

      console.log('🔧 Initializing ConfigService...');
      
      // Detect environment
      this.config.environment = process.env.NODE_ENV || 'development';
      this.config.validation.strict = process.env.REACT_APP_CONFIG_STRICT_MODE === 'true';

      // Load Firebase configuration with fallback
      this.config.firebase = this.getFirebaseConfig();

      // Validate configuration
      const validation = this.validateConfiguration();
      if (!validation.isValid && this.config.validation.strict) {
        throw new Error(`Configuration validation failed: ${validation.errors.join(', ')}`);
      }

      this.isInitialized = true;
      
      ErrorTrackingService.addBreadcrumb(
        'ConfigService initialized',
        'config',
        'info',
        { 
          environment: this.config.environment,
          hasFirebaseConfig: !!this.config.firebase,
          validationStrict: this.config.validation.strict
        }
      );

      console.log('✅ ConfigService initialized successfully');
      return true;

    } catch (error) {
      console.warn('⚠️ ConfigService initialization failed:', error);
      ErrorTrackingService.captureError(error, {
        service: 'ConfigService',
        method: 'initialize',
      });
      return false;
    }
  }

  // Get secure Firebase configuration with fallback (ZERO BREAKING CHANGES)
  static getFirebaseConfig() {
    try {
      // Primary: Environment variables
      const envConfig = {
        apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
        authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
        projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
        storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
        appId: process.env.REACT_APP_FIREBASE_APP_ID,
        measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
      };

      // Check if environment variables are available
      const hasEnvConfig = Object.values(envConfig).every(value => value && value.trim() !== '');

      if (hasEnvConfig) {
        console.log('🔒 Using Firebase config from environment variables');
        return envConfig;
      }

      // Fallback: Hardcoded configuration (preserves existing functionality)
      console.warn('⚠️ Environment variables not found, using fallback Firebase config');
      const fallbackConfig = {
        apiKey: "AIzaSyCLtCvPH6esCTg4aSOuBt1PKzgLTtFaABM",
        authDomain: "awesome-habitat-386117.firebaseapp.com",
        projectId: "awesome-habitat-386117",
        storageBucket: "awesome-habitat-386117.firebasestorage.app",
        messagingSenderId: "652532757120",
        appId: "1:652532757120:web:3582500396a740f020ac65",
        measurementId: "G-KSNCQ5GRV3"
      };

      ErrorTrackingService.addBreadcrumb(
        'Using fallback Firebase configuration',
        'config',
        'warning',
        { source: 'hardcoded_fallback' }
      );

      return fallbackConfig;

    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'ConfigService',
        method: 'getFirebaseConfig',
      });
      
      // Return null to let calling code handle the error
      return null;
    }
  }

  // Enhanced configuration for your existing apiConfig.js integration
  static getEnhancedApiConfig() {
    try {
      // Import your existing apiConfig
      const { apiConfig } = require('../config/apiConfig');
      
      // Enhance with secure Firebase config
      const enhancedConfig = {
        ...apiConfig,
        firebase: this.getFirebaseConfig(),
        
        // Add configuration metadata
        _meta: {
          source: this.config.firebase ? 'environment' : 'fallback',
          environment: this.config.environment,
          timestamp: new Date().toISOString(),
          version: '1.0.0'
        }
      };

      return enhancedConfig;

    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'ConfigService',
        method: 'getEnhancedApiConfig',
      });
      
      // Fallback to original apiConfig
      const { apiConfig } = require('../config/apiConfig');
      return apiConfig;
    }
  }

  // Configuration validation
  static validateConfiguration() {
    const errors = [];
    const warnings = [];

    try {
      // Validate Firebase configuration
      if (!this.config.firebase) {
        errors.push('Firebase configuration is missing');
      } else {
        const requiredFields = ['apiKey', 'authDomain', 'projectId'];
        requiredFields.forEach(field => {
          if (!this.config.firebase[field]) {
            errors.push(`Firebase ${field} is missing`);
          }
        });
      }

      // Environment-specific validation
      if (this.config.environment === 'production') {
        if (!process.env.REACT_APP_FIREBASE_API_KEY) {
          warnings.push('Production environment should use environment variables');
        }
      }

      const result = {
        isValid: errors.length === 0,
        errors,
        warnings,
        timestamp: new Date().toISOString()
      };

      // Log validation results
      if (errors.length > 0) {
        ErrorTrackingService.addBreadcrumb(
          'Configuration validation failed',
          'config',
          'error',
          { errors, warnings }
        );
      } else if (warnings.length > 0) {
        ErrorTrackingService.addBreadcrumb(
          'Configuration validation warnings',
          'config',
          'warning',
          { warnings }
        );
      }

      return result;

    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'ConfigService',
        method: 'validateConfiguration',
      });
      
      return {
        isValid: false,
        errors: ['Configuration validation failed'],
        warnings: [],
        timestamp: new Date().toISOString()
      };
    }
  }

  // Get configuration status for debugging/monitoring
  static getStatus() {
    return {
      initialized: this.isInitialized,
      environment: this.config.environment,
      firebaseConfigSource: this.config.firebase ? (
        process.env.REACT_APP_FIREBASE_API_KEY ? 'environment' : 'fallback'
      ) : 'none',
      validation: this.validateConfiguration(),
      timestamp: new Date().toISOString()
    };
  }

  // Configuration refresh (useful for hot-reloading in development)
  static refresh() {
    this.isInitialized = false;
    return this.initialize();
  }

  // Utility methods for accessing specific configurations
  static getFirebaseConfigValue(key) {
    if (!this.config.firebase) return null;
    return this.config.firebase[key];
  }

  static isEnvironmentConfigured() {
    return !!(process.env.REACT_APP_FIREBASE_API_KEY && 
              process.env.REACT_APP_FIREBASE_AUTH_DOMAIN && 
              process.env.REACT_APP_FIREBASE_PROJECT_ID);
  }

  static isDevelopment() {
    return this.config.environment === 'development';
  }

  static isProduction() {
    return this.config.environment === 'production';
  }

  // Add these methods to your EXISTING ConfigService.js (don't replace existing code)

// ADD this method to your existing ConfigService class:
static debugFirebaseConfiguration() {
  console.log('🔍 FIREBASE CONFIG DEBUG ANALYSIS:');
  
  // Check environment variables parsing
  const envVars = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY ? 
      `✅ Set (${process.env.REACT_APP_FIREBASE_API_KEY.substring(0, 10)}...)` : '❌ Not Loaded',
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '❌ Not Loaded',
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '❌ Not Loaded',
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET ? '✅ Set' : '❌ Not Loaded',
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID ? '✅ Set' : '❌ Not Loaded',
    appId: process.env.REACT_APP_FIREBASE_APP_ID ? 
      `✅ Set (${process.env.REACT_APP_FIREBASE_APP_ID.substring(0, 15)}...)` : '❌ Not Loaded'
  };
  
  console.log('Environment Variables Status:', envVars);
  
  // Check for common .env loading issues
  if (!process.env.REACT_APP_FIREBASE_API_KEY) {
    console.error('❌ CRITICAL: .env variables not being loaded by React');
    console.log('💡 Possible causes:');
    console.log('   - .env file in wrong location (should be at project root)');
    console.log('   - Development server needs restart after .env changes');
    console.log('   - Variable names missing REACT_APP_ prefix');
    console.log('   - .env file has syntax errors or extra spaces');
  }
  
  // Validate current configuration
  const currentConfig = this.getFirebaseConfig();
  console.log('Current Firebase Config Source:', currentConfig ? 'Loaded' : 'Failed to Load');
  
  if (currentConfig) {
    console.log('Config Validation:');
    console.log('- Auth Domain Format:', currentConfig.authDomain?.includes('.firebaseapp.com') ? '✅' : '❌');
    console.log('- API Key Format:', currentConfig.apiKey?.startsWith('AIza') ? '✅' : '❌'); 
    console.log('- Project ID Match:', currentConfig.authDomain?.includes(currentConfig.projectId) ? '✅' : '❌');
  }
  
  return { 
    valid: !!currentConfig && !!process.env.REACT_APP_FIREBASE_API_KEY, 
    config: currentConfig,
    envLoaded: !!process.env.REACT_APP_FIREBASE_API_KEY
  };
}

// ADD this validation method to identify network-specific config issues:
static validateConfigForNetworkIssues(config) {
  console.log('🔍 VALIDATING CONFIG FOR NETWORK ISSUES:');
  
  // Common network-request-failed causes:
  
  // 1. Invalid auth domain format
  if (config.authDomain && !config.authDomain.includes('.firebaseapp.com')) {
    console.warn('⚠️ Auth domain format issue:', config.authDomain);
    return false;
  }
  
  // 2. Invalid API key format
  if (config.apiKey && (config.apiKey.length < 30 || !config.apiKey.startsWith('AIza'))) {
    console.warn('⚠️ API key format issue');
    return false;
  }
  
  // 3. Project ID mismatch with auth domain
  if (config.projectId && config.authDomain && 
      !config.authDomain.includes(config.projectId)) {
    console.warn('⚠️ Project ID / Auth Domain mismatch');
    console.log(`Expected: ${config.projectId}.firebaseapp.com`);
    console.log(`Actual: ${config.authDomain}`);
    return false;
  }

  // 4. Check for undefined/empty values that could cause network errors
  const requiredFields = ['apiKey', 'authDomain', 'projectId'];
  for (const field of requiredFields) {
    if (!config[field] || config[field].toString().includes('undefined')) {
      console.warn(`⚠️ ${field} is missing or contains 'undefined'`);
      return false;
    }
  }
  
  console.log('✅ Configuration passed network validation');
  return true;
}

// ADD this method to auto-repair common configuration issues:
static repairConfigurationMismatch() {
  const config = this.getFirebaseConfig();
  let repaired = false;
  
  if (!config) return null;
  
  console.log('🔧 CHECKING FOR AUTO-REPAIRABLE CONFIG ISSUES:');
  
  // Auto-fix common mismatches
  if (config.projectId && config.authDomain && 
      !config.authDomain.includes(config.projectId)) {
    
    console.log('🔧 Auto-fixing auth domain mismatch');
    config.authDomain = `${config.projectId}.firebaseapp.com`;
    repaired = true;
  }
  
  // Remove any 'undefined' strings that might have crept in
  Object.keys(config).forEach(key => {
    if (config[key] === 'undefined' || config[key] === undefined) {
      console.log(`🔧 Removing undefined value for ${key}`);
      delete config[key];
      repaired = true;
    }
  });
  
  if (repaired) {
    console.log('🔧 Configuration repaired automatically');
  } else {
    console.log('✅ No configuration repairs needed');
  }
  
  return config;
}
}